// Sequence Master (ADR-0017): one numbering engine for every number the product issues.
// Shared so the settings screen previews exactly what the API will issue.

export const SEQUENCE_CODES = ['HOUSE', 'RECEIPT', 'CERT_MEMBERSHIP', 'CERT_NIKAH_NOC', 'CERT_DEATH', 'PRIVACY_REQUEST'] as const;
export type SequenceCode = (typeof SEQUENCE_CODES)[number];

export const SEQUENCE_RESETS = ['NEVER', 'YEARLY', 'MONTHLY'] as const;
export type SequenceReset = (typeof SEQUENCE_RESETS)[number];

export const SEQUENCE_SCOPES = ['NONE', 'WARD'] as const;
export type SequenceScope = (typeof SEQUENCE_SCOPES)[number];

/** Tokens a pattern may use. {SEQn} pads the running number to n digits (3 to 8). */
export const SEQUENCE_TOKENS = ['{YYYY}', '{YY}', '{MM}', '{WARD}', '{SEQ3}', '{SEQ4}', '{SEQ5}', '{SEQ6}', '{SEQ7}', '{SEQ8}'] as const;

/** Mahallus run on Indian time: a number issued at 23:30 UTC on 31 December belongs to 1 January. */
export const SEQUENCE_TIME_ZONE = 'Asia/Kolkata';

export interface SequenceDefault {
  code: SequenceCode;
  nameEn: string;
  nameMl: string;
  pattern: string;
  reset: SequenceReset;
  scope: SequenceScope;
}

/** Created for every new mahallu. The mahallu can change patterns; codes are fixed. */
export const SEQUENCE_DEFAULTS: readonly SequenceDefault[] = [
  { code: 'HOUSE', nameEn: 'House number', nameMl: 'വീട്ടു നമ്പർ', pattern: '{WARD}-{SEQ3}', reset: 'NEVER', scope: 'WARD' },
  { code: 'RECEIPT', nameEn: 'Receipt', nameMl: 'രസീത്', pattern: 'R-{YYYY}-{SEQ5}', reset: 'YEARLY', scope: 'NONE' },
  { code: 'CERT_MEMBERSHIP', nameEn: 'Membership certificate', nameMl: 'അംഗത്വ സർട്ടിഫിക്കറ്റ്', pattern: 'MC-{YYYY}-{SEQ4}', reset: 'YEARLY', scope: 'NONE' },
  { code: 'CERT_NIKAH_NOC', nameEn: 'Nikah NOC', nameMl: 'നികാഹ് എൻ.ഒ.സി', pattern: 'NOC-{YYYY}-{SEQ4}', reset: 'YEARLY', scope: 'NONE' },
  { code: 'CERT_DEATH', nameEn: 'Death and burial record', nameMl: 'മരണ-ഖബറടക്ക രേഖ', pattern: 'DB-{YYYY}-{SEQ4}', reset: 'YEARLY', scope: 'NONE' },
  { code: 'PRIVACY_REQUEST', nameEn: 'Privacy request', nameMl: 'സ്വകാര്യതാ അപേക്ഷ', pattern: 'PR-{YYYY}-{SEQ4}', reset: 'YEARLY', scope: 'NONE' },
];

export interface SequenceRow {
  code: SequenceCode;
  nameEn: string;
  nameMl: string | null;
  pattern: string;
  reset: SequenceReset;
  scope: SequenceScope;
  version: number;
  /** Next value per scope key in the current period ('' when not scoped; ward code when WARD). */
  next: { scopeKey: string; nextValue: number }[];
}

export interface SequenceUpdate {
  pattern?: string;
  reset?: SequenceReset;
  version: number;
}

const TOKEN_RE = /\{[A-Z0-9]+\}/g;
const LITERAL_RE = /^[A-Za-z0-9/_.\- ]*$/;

/** Calendar parts in Indian time. */
export function localParts(at: Date): { yyyy: string; mm: string } {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: SEQUENCE_TIME_ZONE, year: 'numeric', month: '2-digit' }).formatToParts(at);
  return { yyyy: parts.find((p) => p.type === 'year')!.value, mm: parts.find((p) => p.type === 'month')!.value };
}

/** Which counter a number comes from: a new year (or month) starts again at 1. */
export function periodKey(reset: SequenceReset, at: Date): string {
  const { yyyy, mm } = localParts(at);
  return reset === 'YEARLY' ? yyyy : reset === 'MONTHLY' ? `${yyyy}-${mm}` : '';
}

/**
 * Returns an English problem description, or null when the pattern is valid.
 * Rules: 1–40 characters; exactly one {SEQn}; only known tokens; {WARD} only in a ward-scoped
 * series; a yearly series must show the year and a monthly one the month, so numbers never repeat.
 */
export function patternProblem(pattern: string, scope: SequenceScope, reset: SequenceReset): string | null {
  if (pattern.length < 1 || pattern.length > 40) return 'A pattern is 1 to 40 characters.';
  const tokens: string[] = pattern.match(TOKEN_RE) ?? [];
  const unknown = tokens.find((t) => !(SEQUENCE_TOKENS as readonly string[]).includes(t));
  if (unknown) return `Unknown token ${unknown}.`;
  if (!LITERAL_RE.test(pattern.replace(TOKEN_RE, ''))) return 'Use letters, digits, spaces and - / _ . between tokens.';
  if (tokens.filter((t) => t.startsWith('{SEQ')).length !== 1) return 'A pattern needs exactly one running number, e.g. {SEQ4}.';
  if (tokens.includes('{WARD}') && scope !== 'WARD') return '{WARD} can only be used in a ward-numbered series.';
  if (scope === 'WARD' && !tokens.includes('{WARD}')) return 'A ward-numbered series must show {WARD}, or numbers repeat across wards.';
  if (reset !== 'NEVER' && !tokens.some((t) => t === '{YYYY}' || t === '{YY}')) return 'A series that restarts must show the year, or numbers repeat.';
  if (reset === 'MONTHLY' && !tokens.includes('{MM}')) return 'A monthly series must show {MM}, or numbers repeat.';
  return null;
}

/** The number exactly as it will be printed. Values longer than the padding are never cut. */
export function formatSequence(pattern: string, value: number, ctx: { at: Date; ward?: string }): string {
  const { yyyy, mm } = localParts(ctx.at);
  return pattern.replace(TOKEN_RE, (t) => {
    if (t === '{YYYY}') return yyyy;
    if (t === '{YY}') return yyyy.slice(2);
    if (t === '{MM}') return mm;
    if (t === '{WARD}') return ctx.ward ?? '';
    const pad = /^\{SEQ(\d)\}$/.exec(t);
    return pad ? String(value).padStart(Number(pad[1]), '0') : t;
  });
}
